import React, { useState, useEffect } from 'react';
import { CloseIcon, SuccessIcon, InfoIcon } from '@/components/common/icon';
import Button from '@material-ui/core/Button';
import { useSettingsStore } from '@/hooks/useSettingsStore';
import axiosInstance from '@/apis/axiosInstance';
import toast from 'react-hot-toast';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AntSwitch } from './InstantCollaborationSwitch';
import { isMobile } from '@/biz/tool';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
interface Props {
  type?: 'default' | 'switchMode';
}

const InstantCollaboration: React.FC<Props> = ({ type = 'default' }) => {
  const { collaborationConfig, setCollaborationConfig } = useSettingsStore();
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingButtonType, setLoadingButtonType] = useState<
    'auto' | 'manual' | null
  >(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  // mode: manual, auto
  // {
  //   "mode": "manual",
  //   "needScript": false,
  //   "saved": false
  // }

  const postCollaborationConfigData = async ({
    isAuto
  }: {
    isAuto: boolean;
  }) => {
    if (isAuto) {
      navigate('/settings#collaborationSettings');
      return;
    }
    try {
      setLoading(true);
      setLoadingButtonType(isAuto ? 'auto' : 'manual');

      const {
        data: { code, message }
      } = await axiosInstance.post(`/user/collaborationConfig`, {
        mode: isAuto ? 'auto' : 'manual'
      });
      if (code !== 0) {
        toast.error(message);
        return;
      }

      if (isAuto) {
        setSettingsSuccess(true);
        toast.success(
          isAuto
            ? t('home.autoCollaboration', {
                defaultValue:
                  'Collaboration preference updated to Instant Collaboration.'
              })
            : t('home.reviewAllInvites', {
                defaultValue:
                  'Collaboration preference updated to Review All Invites.'
              })
        );
      } else {
        //
      }

      if (type !== 'switchMode') {
        setCollaborationConfig({
          ...collaborationConfig,
          mode: isAuto ? 'auto' : 'manual'
        });

        setTimeout(
          () => {
            setCollaborationConfig({
              ...collaborationConfig,
              saved: true
            });
          },
          isAuto ? 1000 : 0
        );
      } else {
        setCollaborationConfig({
          ...collaborationConfig,
          mode: isAuto ? 'auto' : 'manual'
        });
      }
    } catch (error) {
      console.error('setCollaborationConfig error: ', error);
    } finally {
      setLoading(false);
      setLoadingButtonType(null);
    }
  };
  const handleClose = async () => {
    setCollaborationConfig({
      ...collaborationConfig,
      mode: 'manual',
      saved: true
    });
    try {
      const {
        data: { code, message }
      } = await axiosInstance.post(`/user/collaborationConfig`, {
        mode: 'manual'
      });
      if (code !== 0) {
        toast.error(message);
        return;
      }
    } catch (error) {
      console.error('setCollaborationConfig error: ', error);
    }
  };

  if (collaborationConfig) {
    if (collaborationConfig?.saved) {
      return null;
    }
  } else {
    // 数据未加载
    return null;
  }

  // 根据type渲染不同的UI
  if (type === 'switchMode') {
    return (
      <div
        className={`w-full mt-2 mb-3
          ${isMobile() ? 'px-0' : 'px-4'}
        `}
      >
        <div className="w-full px-4 py-[13px] bg-[#eff1ff] rounded-lg justify-between items-center gap-6 inline-flex">
          <div className="justify-start items-start gap-2 inline-flex flex-1">
            <div className="flex-col justify-start items-start gap-1 inline-flex">
              <div className="text-black text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
                {t('home.instantCollaboration.title', {
                  defaultValue: 'Instant Collaboration'
                })}
              </div>
              <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-tight">
                {t('home.instantCollaboration.description', {
                  defaultValue:
                    'Automatically show interest in collaboration requests sent by Head Creator from trusted, high-quality brands.'
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            {loading ? <CircularProgress size={20} className="mr-2" /> : null}
            <AntSwitch
              checked={collaborationConfig?.mode === 'auto'}
              onChange={(event) =>
                postCollaborationConfigData({ isAuto: event.target.checked })
              }
              name="checkedC"
              disabled={loading}
            />
          </div>
        </div>
      </div>
    );
  }

  // 默认UI
  return (
    <div className={`w-full relative ${isMobile() ? 'px-0' : 'px-20'}`}>
      <div
        className={`w-full bg-[#e2f5ff]
      ${isMobile() ? 'mb-0 p-4 flex flex-col gap-1' : 'rounded-lg mt-4 mb-[-40px] px-4 py-[11px] inline-flex justify-between items-center gap-6'}
    `}
      >
        <div className="justify-start items-start gap-1 inline-flex flex-1">
          <InfoIcon className="w-6 h-6 flex-shrink-0" />
          <div className="flex-col justify-start items-start gap-1 inline-flex">
            <div className="text-black text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
              {t('home.instantCollaboration.title', {
                defaultValue: 'Instant Collaboration'
              })}
            </div>
            <div className="text-black text-sm font-normal font-['Inter'] leading-snug">
              {t('home.instantCollaboration.fullDescription', {
                defaultValue:
                  'Automatically show interest in collaboration requests sent by Head Creator from trusted, high-quality brands. You can change your Instant Collaboration settings anytime in your personal settings.'
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-4 justify-end items-center">
          <Button
            className={`mui-btn-gray max-w-[128px] !h-10 !px-4 !py-2 text-nowrap !flex-shrink-0`}
            onClick={() => postCollaborationConfigData({ isAuto: true })}
            disabled={loading}
          >
            {settingsSuccess ? (
              <div className="flex items-center gap-1 text-[#4CAF50]">
                <SuccessIcon className="w-4 h-4" />{' '}
                {t('common.success', {
                  defaultValue: 'success'
                })}
              </div>
            ) : (
              <>
                {loadingButtonType === 'auto' ? (
                  <CircularProgress size={20} />
                ) : (
                  t('common.goToSettings', {
                    defaultValue: 'Go to settings'
                  })
                )}
              </>
            )}
          </Button>
          <div
            className="w-4 h-4 relative cursor-pointer ml-4"
            onClick={() => handleClose()}
          >
            <CloseIcon className="w-4 h-4 flex-shrink-0" />
          </div>
          {/* {collaborationConfig?.mode !== 'auto' && (
            <Button
              className={`!w-10 !min-w-10 !h-10 !p-0 !rounded-full flex-shrink-0 
                ${loadingButtonType === 'auto' ? 'opacity-0' : ''}
                ${isMobile() ? '!absolute right-[5px] top-[5px]' : ''}
                `}
              onClick={() => postCollaborationConfigData({ isAuto: false })}
              disabled={loading}
            >
              {loadingButtonType === 'manual' ? (
                <CircularProgress size={20} />
              ) : (
                <div className="w-4 h-4 flex flex-col justify-center items-center gap-[2px] transition-transform duration-300">
                  <div className="w-2 h-[2px] bg-black rotate-45 translate-x-[2px]"></div>
                  <div className="w-2 h-[2px] bg-black -rotate-45 -translate-x-[2px]"></div>
                </div>
              )}
            </Button>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default InstantCollaboration;
